// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying fixed assets snapshot.
 *
 * Shows assets that are in fixed asset (in-service) status as of a specific date.
 * Uses react-window for virtualized rendering to handle large datasets efficiently.
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { EmptyState } from '@/components/empty-state';
import { Link } from '@/components/link';
import { TruncatedText } from '@/components/truncated-text';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { FixedAssetSnapshotItem } from '@/report/api/types';
import { SnapshotReportLoader } from '@/report/components/snapshot-report-loader';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// Height of each row in pixels for virtualization
const ROW_HEIGHT = 57;

// Number of rows to pre-render outside the visible area
const OVERSCAN_COUNT = 10;

// Maximum height for the virtualized table body
const MAX_TABLE_HEIGHT = 800;

// Minimum width for horizontal scrolling on narrow screens
const MIN_TABLE_WIDTH = 1200;

// Grid template for consistent column widths between header and rows
const GRID_TEMPLATE_COLUMNS =
  'minmax(150px, 1.5fr) minmax(120px, 1fr) minmax(130px, 1fr) minmax(140px, 1fr) minmax(130px, 1fr) minmax(160px, 1fr) minmax(120px, 1fr)';

interface Props {
  assets: FixedAssetSnapshotItem[];
  isLoading: boolean;
  isAnimating?: boolean;
  onAnimationComplete?: () => void;
  organizationName: string;
}

// Helper function to format percentage values
const formatPercentage = (value: number) => {
  return `${(value * 100).toFixed(1)}%`;
};

// Data passed to the virtualized row renderer, combining assets with the organization name
interface RowData {
  assets: FixedAssetSnapshotItem[];
  organizationName: string;
}

// Virtualized row renderer for the fixed assets table
function VirtualizedRow(props: ListChildComponentProps<RowData>) {
  const asset = props.data.assets[props.index];

  return (
    <Box
      style={props.style}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
      }}
    >
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        <Link
          to="/assets/$assetKey"
          color="primary.main"
          variant="subtitle2"
          params={{ assetKey: asset.key }}
        >
          {asset.serial}
        </Link>
        <TruncatedText
          text={`${asset.item.sku} (${asset.item.name})`}
          variant="caption"
          color="text.secondary"
        />
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.inTransit === true ? (
          <Typography variant="body2" noWrap>
            In transit
          </Typography>
        ) : asset.location ? (
          <>
            <TruncatedText
              text={getEffectiveLocationName(asset.location)}
              variant="body2"
            />
            <TruncatedText
              text={
                asset.location.customer?.name ?? props.data.organizationName
              }
              variant="caption"
              color="text.secondary"
            />
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {asset.inServiceDate ? (
          <>
            <Typography variant="body2">
              {asset.inServiceDate.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            {asset.daysInService !== null && (
              <ConditionalTooltip
                arrow
                tooltipText={
                  asset.daysOfUsefulLife !== null
                    ? `${formatPercentage(
                        asset.daysInService / asset.daysOfUsefulLife
                      )} of useful life (${formatNumber(asset.daysOfUsefulLife)} days)`
                    : undefined
                }
              >
                <Typography variant="caption" color="text.secondary">
                  {pluralize(asset.daysInService, {
                    singular: 'day in service',
                    plural: 'days in service',
                  })}
                </Typography>
              </ConditionalTooltip>
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {asset.depreciableAmount !== null ? (
          <Tooltip
            arrow
            title={
              <>
                Capitalized costs:{' '}
                {formatCurrencyValue(asset.accounting.capitalizedCosts)}
                <br />
                Salvage value:{' '}
                {formatCurrencyValue(asset.accounting.salvageValue)}
              </>
            }
          >
            <Typography variant="body2">
              {formatAmountWithCurrency(
                asset.depreciableAmount,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {asset.endOfUsefulLife ? (
          <>
            <Typography variant="body2">
              {asset.endOfUsefulLife.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={
                asset.daysOfUsefulLife !== null
                  ? `${formatNumber(asset.daysOfUsefulLife)} days`
                  : undefined
              }
            >
              {asset.accounting.usefulLife !== null && (
                <Typography variant="caption" color="text.secondary">
                  {pluralize(asset.accounting.usefulLife, {
                    singular: 'month',
                    plural: 'months',
                  })}
                </Typography>
              )}
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {asset.accumulatedDepreciation !== null &&
        asset.depreciableAmount !== null ? (
          <>
            <Typography variant="body2">
              {formatAmountWithCurrency(
                asset.accumulatedDepreciation,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={`${formatAmountWithCurrency(asset.accumulatedDepreciation, asset.accounting.costCurrencyCode)} depreciated of ${formatAmountWithCurrency(asset.depreciableAmount, asset.accounting.costCurrencyCode)} depreciable`}
            >
              <Typography variant="caption" color="text.secondary">
                {formatPercentage(
                  parseFloat(asset.accumulatedDepreciation) /
                    parseFloat(asset.depreciableAmount)
                )}{' '}
                depreciated
              </Typography>
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {asset.bookValue !== null &&
        asset.accounting.capitalizedCosts !== null ? (
          <>
            <Typography variant="body2">
              {formatAmountWithCurrency(
                asset.bookValue,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={
                `${formatAmountWithCurrency(asset.bookValue, asset.accounting.costCurrencyCode)} remaining of ${formatCurrencyValue(asset.accounting.capitalizedCosts)} capitalized costs` +
                ` ${asset.accounting.salvageValue ? ` (including ${formatCurrencyValue(asset.accounting.salvageValue)} salvage value)` : ''}`
              }
            >
              <Typography variant="caption" color="text.secondary">
                {formatPercentage(
                  parseFloat(asset.bookValue) /
                    asset.accounting.capitalizedCosts.amount.toNumber()
                )}{' '}
                remaining
              </Typography>
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
    </Box>
  );
}

export function FixedAssetsSnapshotTable(props: Props) {
  // Calculate the height for the virtualized list
  const listHeight = Math.min(
    props.assets.length * ROW_HEIGHT,
    MAX_TABLE_HEIGHT
  );

  if (props.isLoading || props.isAnimating) {
    return (
      <SnapshotReportLoader
        reportType="fixed"
        isDataReady={!props.isLoading}
        onComplete={props.onAnimationComplete}
      />
    );
  }

  if (props.assets.length === 0) {
    return (
      <EmptyState
        title="No fixed assets found"
        message="There were no assets on this date with an in-service date that hadn't been sold or scrapped"
      />
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      {/* Scrollable inner container with minimum width */}
      <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
        {/* Header row using CSS grid to match virtualized rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.100',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="overline">Serial</Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Location
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            In-service date
          </Typography>
          <Tooltip arrow title="Cost basis minus salvage value">
            <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
              Depreciable amount
            </Typography>
          </Tooltip>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            End of useful life
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Accumulated depreciation
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Book value
          </Typography>
        </Box>

        {/* Virtualized table body */}
        <FixedSizeList
          height={listHeight}
          width="100%"
          itemSize={ROW_HEIGHT}
          itemCount={props.assets.length}
          overscanCount={OVERSCAN_COUNT}
          itemData={{
            assets: props.assets,
            organizationName: props.organizationName,
          }}
        >
          {VirtualizedRow}
        </FixedSizeList>
      </Box>
    </Paper>
  );
}
